import { FC, ReactNode } from 'react';

import { Stack, SxProps, Typography } from '@mui/material';

import { useIsMobile } from '~components/hooks/utils/useIsMobile';
import { primaryColors } from '~components/theme/styles/colors';

import { LoadSkeleton, LoadSkeletonProps } from '../loadSkeleton';
import { HelpTooltip } from '../tooltip/helpTooltip';

export type GenericLabelVariant = 'horizontal' | 'vertical';
export type GenericLabelAlignment = 'start' | 'end' | 'left' | 'right' | 'center';

export interface GenericInfoLabelToolTip {
  content: ReactNode;
  placement: 'value-left' | 'value' | 'title-left' | 'title';
}

export interface GenericInfoLabelProps {
  variant?: GenericLabelVariant;
  align?: GenericLabelAlignment;
  labelTitle?: ReactNode;
  labelTitleSx?: SxProps;
  labelTitleVariant?: any;
  labelValue?: ReactNode;
  labelValueSx?: SxProps;
  toolTipIconColor?: string;
  labelValueVariant?: any;
  labelValueUnitSx?: SxProps;
  labelValueUnit?: ReactNode;
  labelValueAnnotation?: string;
  labelValueAnnotationSx?: SxProps;
  toolTip?: GenericInfoLabelToolTip;
  sx?: SxProps;
  isLoading?: boolean;
  skeletonProps?: Omit<LoadSkeletonProps, 'isLoading'>;
  verticalOnMobile?: boolean;
}

export const GenericInfoLabel: React.FC<GenericInfoLabelProps> = ({
  labelTitleSx,
  labelTitleVariant = 'label3',
  labelTitle,
  labelValue,
  labelValueSx,
  labelValueUnit,
  labelValueUnitSx,
  labelValueAnnotation,
  labelValueVariant = 'label3',
  toolTipIconColor = primaryColors[600],
  variant = 'horizontal',
  align = 'start',
  toolTip,
  sx,
  isLoading,
  skeletonProps,
  verticalOnMobile,
}) => {
  const isMobile = useIsMobile();

  const direction =
    variant === 'vertical' || (variant === 'horizontal' && isMobile && verticalOnMobile) ? 'column' : 'row';

  return (
    <Stack
      spacing={0.25}
      direction={direction}
      display={'flex'}
      justifyContent={variant === 'horizontal' ? 'space-between' : align}
      textAlign={align}
      width={'100%'}
      sx={sx}
      data-testid={typeof labelTitle === 'string' ? labelTitle.replaceAll(' ', '-') : undefined}
    >
      <Stack direction={'row'} alignItems={'center'} justifyContent={align} spacing={0.5}>
        {toolTip && toolTip.placement === 'title-left' ? (
          <HelpTooltip tooltipSz={12} color={toolTipIconColor} title={toolTip.content} />
        ) : null}

        {labelTitle ? (
          typeof labelTitle === 'string' ? (
            <Typography
              variant={labelTitleVariant}
              color={primaryColors[600]}
              sx={{ mb: verticalOnMobile ? ['4px', '0px'] : undefined, ...labelTitleSx }}
            >
              {labelTitle}
            </Typography>
          ) : (
            labelTitle
          )
        ) : null}

        {toolTip && toolTip.placement === 'title' ? (
          <HelpTooltip tooltipSz={12} color={toolTipIconColor} title={toolTip.content} />
        ) : null}
      </Stack>

      <Stack direction={'row'} spacing={0.5} alignItems={'center'} justifyContent={align}>
        {labelValue ? (
          typeof labelValue === 'string' ? (
            <LoadSkeleton width={50} height={16} isLoading={!!isLoading} {...skeletonProps}>
              <Typography sx={labelValueSx} variant={labelValueVariant}>
                {labelValue}
              </Typography>
            </LoadSkeleton>
          ) : (
            <LoadSkeleton width={50} height={16} isLoading={!!isLoading} {...skeletonProps}>
              {labelValue}
            </LoadSkeleton>
          )
        ) : null}

        {labelValueUnit ? (
          typeof labelValueUnit === 'string' || typeof labelValueUnit === 'number' ? (
            <Typography sx={labelValueUnitSx} variant={labelValueVariant}>
              {labelValueUnit}
            </Typography>
          ) : (
            labelValueUnit
          )
        ) : null}

        {toolTip && toolTip.placement === 'value-left' ? (
          <HelpTooltip tooltipSz={12} color={toolTipIconColor} title={toolTip.content} />
        ) : null}

        {labelValueAnnotation && variant === 'horizontal' ? (
          <Typography variant={'label3'} color={primaryColors[600]} justifyContent={align}>
            {`${labelValueAnnotation}`}
          </Typography>
        ) : null}

        {toolTip && toolTip.placement === 'value' ? (
          <HelpTooltip tooltipSz={14} color={toolTipIconColor} title={toolTip.content} />
        ) : null}
      </Stack>

      {labelValueAnnotation && variant === 'vertical' ? (
        <LoadSkeleton isLoading={!!isLoading}>
          <Typography variant={'label3'} color={primaryColors[600]} justifyContent={align}>
            {labelValueAnnotation}
          </Typography>
        </LoadSkeleton>
      ) : null}
    </Stack>
  );
};
